import { Dispatch, SetStateAction } from 'react';
import { DataGrid, GridRowSelectionModel } from '@mui/x-data-grid';
import { useAtom } from 'jotai';
import { Box, Button } from '@mui/material';
import { Columns, PaginationModel, Rows } from './types';
import { variantFilterAtom } from './variantFilterAtom';
import VariantSelectionDropdown from './VariantDropdown';
import VariantDetailsPage from './VariantDetailsPage';
import { useVariantDrawer } from './hooks/useVariantDrawer';

interface DataGridProps {
  rows: Rows[];
  columns: Columns[];
  loading: boolean;
  dataLoading: boolean;
  rowCount: number;
  paginationModel: PaginationModel;
  handlePaginationModelChange: (model: PaginationModel) => void;
  onAddVariant: () => void;
  setSelectedRows: Dispatch<SetStateAction<GridRowSelectionModel>>;
  selectedRows: GridRowSelectionModel;
  onRemoveVariant: () => void;
  outputID: string;
  allowValidateVariant: boolean;
}

const ResultGrid = ({
  rows,
  columns,
  loading,
  dataLoading,
  rowCount,
  paginationModel,
  handlePaginationModelChange,
  setSelectedRows,
  onAddVariant,
  selectedRows,
  onRemoveVariant,
  outputID,
  allowValidateVariant,
}: DataGridProps) => {
  // eslint-disable-next-line max-len
  const [variantFilter] = useAtom(variantFilterAtom); // 0 = default, 1 = selected variants, 2 = unselected variants

  const {
    drawerHeight,
    drawerOpen,
    isDragging,
    selectedVariation,
    variantDetails,
    newComment,
    setNewComment,
    handleCloseDrawer,
    handleMouseDown,
    onCommentVariant,
    handleDeleteComment,
    handleRowClick,
    variantComments,
    onValidateVariant,
    onRejectVariant,
  } = useVariantDrawer(outputID);

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: 2,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <VariantSelectionDropdown />
        {/* Left-aligned Apply button */}
        {variantFilter === 0 || variantFilter === 2 ? (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={onAddVariant}
          >
            Add Variants
          </Button>
        ) : (
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={onRemoveVariant}
          >
            Remove Variants
          </Button>
        )}
      </Box>

      <DataGrid
        paginationMode="server"
        rows={rows}
        columns={columns}
        loading={loading || dataLoading}
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        onRowClick={handleRowClick}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
        checkboxSelection
        sx={{
          '& .MuiDataGrid-row': { cursor: 'pointer' },
        }}
        keepNonExistentRowsSelected
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={(newSelection) =>
          setSelectedRows(newSelection)
        }
      />

      {/* Bottom Drawer for Comments */}
      <VariantDetailsPage
        drawerHeight={drawerHeight}
        handleCloseDrawer={handleCloseDrawer}
        drawerOpen={drawerOpen}
        isDragging={isDragging}
        handleMouseDown={handleMouseDown}
        variantDetails={variantDetails}
        selectedVariation={selectedVariation}
        newComment={newComment}
        handleDeleteComment={handleDeleteComment}
        setNewComment={setNewComment}
        allowValidateVariant={allowValidateVariant}
        allowRejectVariant={false}
        onValidateVariant={onValidateVariant}
        onRejectVariant={onRejectVariant}
        onCommentVariant={onCommentVariant}
        variantComments={variantComments}
      />
    </Box>
  );
};

export default ResultGrid;
